import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { ApiError } from "@/lib/api";
import { isMetaRateLimitError, shouldRetryMetaQuery } from "@/lib/api/metaRateLimit";
import { useToast } from "@/hooks/use-toast";
import { showApiError } from "@/lib/errors";
import { getAiAnalyticsUrl, getAiAnalyticsBaseUrl } from "@/lib/api/aiAnalyticsUrl";
import { getOrganizationId } from "@/lib/api/organization";
import { useBusinessAccountContext } from "@/hooks/useBusinessAccountContext";
import { uploadAdVideoToS3 } from "@/lib/ads/adVideoUpload";

const ADS_API = "/ads";

// === API Functions ===

const adsApi = {
  // Setup
  getSetupStatus: (businessAccountId?: string | null) =>
    apiClient.get(
      `${ADS_API}/setup/status${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getOAuthUrl: () => apiClient.get(`${ADS_API}/setup/oauth-url`),
  handleCallback: (code: string) => apiClient.post(`${ADS_API}/setup/callback`, { code }),
  connectAdAccount: (data: any) => apiClient.post(`${ADS_API}/setup/connect`, data),
  getBalance: (businessAccountId?: string | null) =>
    apiClient.get(
      `${ADS_API}/setup/balance${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getAvailableAdAccounts: (opts?: { includeBusinessScoped?: boolean }) =>
    apiClient.get(
      `${ADS_API}/setup/ad-accounts${
        opts?.includeBusinessScoped ? "?include_business_scoped=true" : ""
      }`
    ),
  switchAdAccount: (data: any) => apiClient.post(`${ADS_API}/setup/switch`, data),
  // `adAccountId` targets ONE account. Omitted → the active one (legacy behaviour).
  disconnect: (adAccountId?: string, businessAccountId?: string | null) => {
    const params = new URLSearchParams();
    if (adAccountId) params.set("ad_account_id", adAccountId);
    if (businessAccountId) params.set("business_account_id", businessAccountId);
    const qs = params.toString();
    return apiClient.delete(`${ADS_API}/setup/disconnect${qs ? `?${qs}` : ""}`);
  },

  // Campaigns
  getCampaigns: (params: any) => apiClient.get(`${ADS_API}/campaigns`, { params }),
  createCampaign: (data: any) => apiClient.post(`${ADS_API}/campaigns`, data),

  // Run As Ad (T1.1 / T1.2)
  createAdFromPost: (data: any) => apiClient.post(`${ADS_API}/from-post`, data),
  recommendAd: (data: any) => apiClient.post(`${ADS_API}/recommend`, data),

  // Revenue Attribution (T2.3 / T2.4)
  getAttribution: (params?: { from?: string; to?: string }) =>
    apiClient.get(`${ADS_API}/attribution`, params ? { params } : undefined),

  // CRM lead quality per ad (Phase 1) — stage-name buckets for Ads tab overlay
  getLeadQuality: (params?: { from?: string; to?: string }) =>
    apiClient.get(`${ADS_API}/lead-quality`, params ? { params } : undefined),

  // Per-stage CRM counts for one Meta ad (Ads-tab drawer)
  getLeadQualityStages: (params: {
    ad_id?: string;
    ad_name?: string;
    from?: string;
    to?: string;
  }) => apiClient.get(`${ADS_API}/lead-quality/stages`, { params }),

  // AI Suggest Ad (T3.3) — "should I promote this post?"
  suggestFromPost: (data: { post: Record<string, any>; baseline?: Record<string, any>; business_account_id?: string }) =>
    apiClient.post(`${ADS_API}/suggest-from-post`, data),
  // AI Suggest Ad (Phase 3) — pending suggestions list + dismiss
  getSuggestions: () => apiClient.get(`${ADS_API}/suggestions`),
  dismissSuggestion: (id: string) => apiClient.post(`${ADS_API}/suggestions/${id}/dismiss`),
  getCampaign: (id: string) => apiClient.get(`${ADS_API}/campaigns/${id}`),
  updateCampaign: (id: string, data: any) => apiClient.patch(`${ADS_API}/campaigns/${id}`, data),
  deleteCampaign: (id: string) => apiClient.delete(`${ADS_API}/campaigns/${id}`),
  syncCampaign: (id: string) => apiClient.post(`${ADS_API}/campaigns/${id}/sync`),
  duplicateCampaign: (id: string) => apiClient.post(`${ADS_API}/campaigns/${id}/duplicate`),

  // Insights
  getCampaignInsights: (id: string, params: any) =>
    apiClient.get(`${ADS_API}/campaigns/${id}/insights`, { params }),
  getLeadsChart: (id: string, params: any) =>
    apiClient.get(`${ADS_API}/campaigns/${id}/leads-chart`, { params }),

  // Search
  searchInterests: (query: string) => apiClient.post(`${ADS_API}/search/interests`, { query }),
  searchLocations: (query: string, mode?: string) =>
    apiClient.post(`${ADS_API}/search/locations`, mode ? { query, mode } : { query }),
  searchBehaviors: (query: string) => apiClient.post(`${ADS_API}/search/behaviors`, { query }),
  getConnectedInstagram: () => apiClient.get(`${ADS_API}/setup/instagram`),
  getCplEstimate: (params: { objective?: string; daily_budget?: number }) =>
    apiClient.get(`${ADS_API}/cpl-estimate`, { params }),
  getPlatformBreakdown: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/platforms`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  searchJobTitles: (query: string) => apiClient.post(`${ADS_API}/search/job-titles`, { query }),
  // Detailed targeting
  browseTargeting: (cls: string, query?: string) =>
    apiClient.post(`${ADS_API}/targeting/browse`, query ? { class: cls, query } : { class: cls }),
  searchAllTargeting: (query: string) => apiClient.post(`${ADS_API}/targeting/search`, { query }),
  getTargetingEnums: () => apiClient.get(`${ADS_API}/targeting/enums`),
  estimateAudience: (targetingSpec: unknown, optimizationGoal?: string) =>
    apiClient.post(`${ADS_API}/targeting/estimate`, {
      targeting_spec: targetingSpec,
      ...(optimizationGoal ? { optimization_goal: optimizationGoal } : {}),
    }),

  // Audiences
  getAudiences: () => apiClient.get(`${ADS_API}/audiences`),
  createCustomAudience: (data: any) => apiClient.post(`${ADS_API}/audiences/custom`, data),
  createAudienceFromContacts: (data: any) =>
    apiClient.post(`${ADS_API}/audiences/from-contacts`, data),
  createLookalikeAudience: (data: any) => apiClient.post(`${ADS_API}/audiences/lookalike`, data),
  getAudiencePresets: () => apiClient.get(`${ADS_API}/audiences/presets`),
  saveAudiencePreset: (data: any) => apiClient.post(`${ADS_API}/audiences/presets`, data),

  // Flows
  getFlows: () => apiClient.get(`${ADS_API}/flows`),

  // Fetch from Meta
  // `range` (YYYY-MM-DD since/until) is Meta's time_range and WINS over
  // date_preset server-side. Sent only when both bounds are present.
  fetchAdsFromMeta: (
    datePreset?: string,
    range?: { since: string; until: string }
  ) => {
    const params: Record<string, string> = {};
    if (datePreset) params.date_preset = datePreset;
    if (range?.since && range?.until) {
      params.since = range.since;
      params.until = range.until;
    }
    return apiClient.get(
      `${ADS_API}/fetch-from-meta`,
      Object.keys(params).length ? { params } : undefined
    );
  },
  getMultiAccountSummary: (adAccountIds: string[], datePreset?: string) =>
    apiClient.post(`${ADS_API}/multi-account-summary`, {
      ad_account_ids: adAccountIds,
      date_preset: datePreset,
    }),
  getBreakdown: (dimension: string, datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/breakdown`, {
      params: { dimension, ...(datePreset ? { date_preset: datePreset } : {}) },
    }),
  getFunnel: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/funnel`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getAnomalies: () => apiClient.get(`${ADS_API}/insights/anomalies`),
  getWeeklyDigest: () => apiClient.get(`${ADS_API}/insights/weekly-digest`),
  buildCustomReport: (body: {
    level: string; metrics: string[]; sortBy?: string; sortDir?: string; date_preset?: string;
  }) => apiClient.post(`${ADS_API}/insights/custom-report`, body),
  getCreativeInsights: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/creative`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getSpendPacing: () => apiClient.get(`${ADS_API}/insights/pacing`),
  getRevenuePerLead: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/revenue-per-lead`, datePreset ? { params: { date_preset: datePreset } } : undefined),

  // Meta ad actions
  updateMetaAd: (metaAdId: string, data: any) => apiClient.post(`${ADS_API}/meta-ads/${metaAdId}`, data),
  updateMetaCampaign: (metaCampaignId: string, data: any) => apiClient.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  // Image upload
  uploadImage: (imageUrl: string) => apiClient.post(`${ADS_API}/upload-image`, { image_url: imageUrl }),
  uploadImageFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-image-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },
  uploadVideoFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-video-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Video upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },

  // AI generation
  generateAdCopy: (data: any) => apiClient.post(`${ADS_API}/generate-copy`, data),

  // AdSet actions
  updateMetaAdSet: (metaAdSetId: string, data: any) => apiClient.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  // Single ad detail (fresh from Meta)
  getMetaAdDetail: (metaAdId: string) => apiClient.get(`${ADS_API}/meta-ads/${metaAdId}`),

  // Campaign detail (drill-down)
  getMetaCampaignDetail: (metaCampaignId: string) =>
    apiClient.get(`${ADS_API}/meta-campaigns/${metaCampaignId}/detail`),

  // Ad Set detail (drill-down)
  getMetaAdSetDetail: (metaAdSetId: string) =>
    apiClient.get(`${ADS_API}/meta-adsets/${metaAdSetId}/detail`),

  // Ad insights with date range
  getMetaAdInsights: (metaAdId: string, params: any) =>
    apiClient.get(`${ADS_API}/meta-ads/${metaAdId}/insights`, { params }),

  // AI assistant
  aiAssistant: (data: any) => apiClient.post(`${ADS_API}/ai-assistant`, data),

  // Update Meta ad set (budget, schedule)
  updateMetaAdSetData: (metaAdSetId: string, data: any) =>
    apiClient.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  // Update Meta campaign data
  updateMetaCampaignData: (metaCampaignId: string, data: any) =>
    apiClient.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  // Business & Funding
  getBusinesses: () => apiClient.get(`${ADS_API}/businesses`),
  createAdAccount: (data: any) => apiClient.post(`${ADS_API}/create-ad-account`, data),
  getFundingDetails: () => apiClient.get(`${ADS_API}/funding`),

  // Lead suppression
  getSuppressionStatus: () => apiClient.get(`${ADS_API}/setup/suppression/status`),
  enableSuppression: () => apiClient.post(`${ADS_API}/setup/suppression/enable`, {}),
  applySuppressionToActive: () =>
    apiClient.post(`${ADS_API}/setup/suppression/apply-to-active`, {}),
  backfillSuppression: () => apiClient.post(`${ADS_API}/setup/suppression/backfill`, {}),

  // Catalogs
  createProductCatalog: (data: { business_id: string; name: string; vertical?: string }) =>
    apiClient.post(`${ADS_API}/catalogs`, data),
  addProductToCatalog: (catalogId: string, product: any) =>
    apiClient.post(`${ADS_API}/catalogs/${catalogId}/products`, product),
  getProductCatalogs: () => apiClient.get(`${ADS_API}/catalogs`),
  getCatalogProductSets: (catalogId: string) =>
    apiClient.get(`${ADS_API}/catalogs/${catalogId}/product-sets`),
  getCatalogProducts: (catalogId: string) =>
    apiClient.get(`${ADS_API}/catalogs/${catalogId}/products`),

  // Meta Leads
  getLeadForms: () => apiClient.get(`${ADS_API}/leads/forms`),
  createLeadForm: (data: any) => apiClient.post(`${ADS_API}/leads/forms`, data),
  getLeadFormLeads: (formId: string, params?: any) =>
    apiClient.get(`${ADS_API}/leads/forms/${formId}/leads`, { params }),
  getAllPageLeads: (params?: any) => apiClient.get(`${ADS_API}/leads`, { params }),
  getLeadGenSubscriptionStatus: () => apiClient.get(`${ADS_API}/leads/subscription-status`),
  resubscribeLeadGenPages: () => apiClient.post(`${ADS_API}/leads/resubscribe`, {}),

  // Ad Account management
  getAgencies: () => apiClient.get(`${ADS_API}/setup/agencies`),
  getAdvertisableApplications: () => apiClient.get(`${ADS_API}/setup/advertisable-applications`),
  getAssignedUsers: () => apiClient.get(`${ADS_API}/setup/assigned-users`),
  updateAdAccount: (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) =>
    apiClient.patch(`${ADS_API}/setup/account`, data),
  getReachFrequencyPredictions: () => apiClient.get(`${ADS_API}/setup/reach-frequency-predictions`),
  getConnectedInstagramAccounts: () => apiClient.get(`${ADS_API}/setup/connected-instagram-accounts`),

  // Ad copy & previews
  copyAd: (adId: string, data?: { status_option?: string }) =>
    apiClient.post(`${ADS_API}/meta-ads/${adId}/copy`, data ?? {}),
  getAdPreviews: (adId: string, params: { ad_format: string; creative_feature?: string }) =>
    apiClient.get(`${ADS_API}/meta-ads/${adId}/previews`, { params }),

  // Async insights
  createInsightsReport: (data: { object_id: string; level?: string; date_preset?: string; time_range?: any; breakdowns?: string; fields?: string }) =>
    apiClient.post(`${ADS_API}/insights/report`, data),
  pollInsightsReport: (reportRunId: string) =>
    apiClient.get(`${ADS_API}/insights/report/${reportRunId}`),

  // Targeting
  getBroadTargetingCategories: () => apiClient.get(`${ADS_API}/targeting/broad-categories`),

  // Pixels / Datasets
  getAdPixels: () => apiClient.get(`${ADS_API}/pixels`),
  createAdPixel: (data: { name: string }) => apiClient.post(`${ADS_API}/pixels`, data),

  // CAPI / conversions
  getCapiDatasets: () => apiClient.get(`${ADS_API}/capi/datasets`),
  getCustomConversions: () => apiClient.get(`${ADS_API}/capi/custom-conversions`),

  // Page / IG posts (for ad media picker)
  getPagePosts: (limit = 25) => apiClient.get(`${ADS_API}/page-posts`, { params: { limit } }),
  getInstagramMedia: (limit = 25) => apiClient.get(`${ADS_API}/instagram-media`, { params: { limit } }),

  // Video upload by URL
  uploadVideoByUrl: (data: { video_url: string; title?: string; fallback_url?: string }) =>
    apiClient.post(`${ADS_API}/upload-video-url`, data),
  getVideoStatus: (videoId: string) =>
    apiClient.get(`${ADS_API}/videos/${encodeURIComponent(videoId)}/status`),

  generateFromImage: async (file: File, organizationId?: string | null) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-image", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }

    return json;
  },

  /**
   * AI campaign generation from a video clip.
   *
   * Accepts EITHER a File (multipart field `video`) OR a URL (multipart field
   * `video_url`). The backend extracts frames, runs GPT-4V on them, and
   * returns the same MetaAdCampaignSuggestionResponse shape as the image
   * variant — so callers can reuse the AiMetaAdsResponse handler.
   *
   * Limits (enforced server-side):
   *   - video ≤ 100 MB
   *   - duration ≤ 10 min
   *   - MIME starts with video/ (mp4 / mov / webm / mkv / avi / mpeg)
   *
   * Expect 30–60s latency depending on duration — frame extraction + LLM call.
   */
  generateFromVideo: async (
    input: { file?: File; videoUrl?: string },
    organizationId?: string | null
  ) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }
    if (!input.file && !input.videoUrl) {
      throw new Error("Provide either a video file or a video_url");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    if (input.file) {
      formData.append("video", input.file);
    } else if (input.videoUrl) {
      formData.append("video_url", input.videoUrl);
    }
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-video", "root"), {
      method: "POST",
      // Explicitly DON'T set Content-Type — fetch must pick its own multipart
      // boundary. Setting it manually breaks the upload.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }
    return json;
  },
};

// === Hooks ===

export function useAdsSetupStatus() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useQuery({
    queryKey: ["ads", "setup-status", businessAccountId],
    queryFn: async () => {
      const res = await adsApi.getSetupStatus(businessAccountId);
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: 0,
  });
}

/**
 * Leadgen webhook status for every connected Page.
 *
 * 🔴 THE MOST EXPENSIVE READ IN THIS FILE, PER MOUNT. The backend does a
 * `GET /me/accounts` and then ONE subscription check PER PAGE, so a single mount
 * costs `1 + N` Meta calls — an org with 12 pages spends 13 from a bucket of
 * ~4,080 an hour for the entire workspace. At `staleTime: 60s` a user sitting on
 * the Settings tab re-spent that every minute.
 *
 * 15 minutes is safe because subscription state only changes on an action wenext
 * itself performs, and each of those invalidates this exact key:
 *   - `useResubscribeLeadGenPages` (the button in LeadGenSubscriptionPanel)
 *   - `useOAuthCallback` / `useAdsSetupConnect` (a reconnect can add pages)
 *   - `useSwitchAdAccount` / `useAdsDisconnect` (invalidate all of `["ads"]`)
 * So the user never reads a stale answer after doing the thing that changes it.
 */
export function useLeadGenSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: ["ads", "leadgen-subscription-status"],
    queryFn: async () => {
      const res = await adsApi.getLeadGenSubscriptionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    // Alt-tabbing back must not re-spend `1 + N` calls. (The app-wide default is
    // already false for focus; both are pinned here because this query is costly
    // enough that a future default change must not silently re-enable them.)
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  });
}

export function useResubscribeLeadGenPages() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.resubscribeLeadGenPages();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      const subscribed = data?.subscribed?.length ?? 0;
      const failed = data?.failed?.length ?? 0;
      toast({
        variant: failed > 0 ? "warning" : "success",
        title:
          failed > 0
            ? `Subscribed ${subscribed} page(s); ${failed} need reconnecting`
            : `Subscribed ${subscribed} page(s) to lead capture`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to subscribe pages" } });
    },
  });
}

/**
 * Ad-account balance. LIVE Meta call on every request —
 * `AdsService.getBalance` does `metaApi.getAdAccountBalance(...)` and only then
 * writes `balance_cache`; nothing serves the cached column on this path.
 *
 * Mounted on the Dashboard, Analytics, the Meta Ads header bar, both
 * BalanceWidget call sites and the Instagram ads dashboard — so at
 * `staleTime: 30s` an ordinary navigation loop around the app re-spent a Meta
 * call every half minute for a number that moves when the user tops up.
 *
 * 3 minutes, not longer, precisely because "I just added funds" is the one case
 * where a user knows the figure is wrong. That case is covered explicitly:
 *   - `useSwitchAdAccount` / `useAdsDisconnect` invalidate all of `["ads"]`
 *   - the Add-funds modal invalidates `["ads", "balance"]` when it closes
 *   - InstagramAdsDashboard still calls `balance.refetch()` after a switch
 */
export function useAdsBalance(enabled = true) {
  return useQuery({
    queryKey: ["ads", "balance"],
    queryFn: async () => {
      const res = await adsApi.getBalance();
      return res.data;
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaigns(
  params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "campaigns", params],
    queryFn: async () => {
      const res = await adsApi.getCampaigns(params);
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaign(id: string | null) {
  return useQuery({
    queryKey: ["ads", "campaign", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaign(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useAdsCampaignInsights(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "insights", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaignInsights(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsLeadsChart(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "leads-chart", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getLeadsChart(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsFlows() {
  return useQuery({
    queryKey: ["ads", "flows"],
    queryFn: async () => {
      const res = await adsApi.getFlows();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdsFetchedFromMeta(
  datePreset: string = "maximum",
  // Positional slot kept for existing callers that pass an account id. It is
  // NOT forwarded — this endpoint has always used the org's active account.
  // Declared so those call sites type-check rather than silently mismatching.
  _accountId?: string,
  range?: { since: string; until: string }
) {
  // A custom range is part of the cache key, or switching between two ranges
  // would serve the first one's data.
  const rangeKey = range?.since && range?.until ? `${range.since}:${range.until}` : null;
  return useQuery({
    queryKey: ["ads", "fetched-from-meta", datePreset, rangeKey],
    queryFn: async () => {
      const res = await adsApi.fetchAdsFromMeta(datePreset, range);
      return res.data;
    },
    // The heaviest single Meta read in the product (campaigns + ad sets + ads +
    // per-object insights, paginated). Four tabs on /home/ads each mount this
    // and each re-triggers it via a `!data` effect, so the only thing standing
    // between tab-flipping and repeated full pulls is how long the entry lives.
    // At `staleTime: 60s` / the 5-min default `gcTime`, leaving the Ads page for
    // five minutes and coming back paid for the whole thing again.
    //
    // Every mutation that can change these rows already invalidates
    // `["ads", "fetched-from-meta"]` (useUpdateMetaAd / useUpdateMetaCampaign /
    // useUpdateMetaAdSet / useCopyAd) or all of `["ads"]` (useSyncCampaign /
    // useSwitchAdAccount / useAdsDisconnect), and EVERY tab that renders this
    // data keeps its own explicit Refresh button wired to `refetch()` — so a
    // longer window can always be cleared by hand.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: false, // Only fetch on demand
  });
}

export const BREAKDOWN_DIMENSIONS = [
  { key: "age_gender", label: "Age & Gender" },
  { key: "placement", label: "Placement" },
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "device", label: "Device" },
  { key: "time_of_day", label: "Time of Day" },
] as const;

export type BreakdownDimensionKey = (typeof BREAKDOWN_DIMENSIONS)[number]["key"];

export interface BreakdownRow {
  bucket: string;
  keys: Record<string, string>;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  cost_per_lead: number | null;
}

export interface BreakdownResult {
  dimension: BreakdownDimensionKey;
  label: string;
  rows: BreakdownRow[];
  totals: { spend: number; impressions: number; clicks: number; leads: number };
  date_preset: string;
  currency: string;
  ad_account_id: string;
}

/**
 * Breakdown insights for one dimension. Fetched on demand from Meta (live,
 * rate-limited) so it is disabled until `enabled` is true — the tab enables it
 * once mounted. Cached 10 min to avoid re-hitting Meta on every dimension flip
 * back-and-forth.
 */
export function useAdsBreakdown(
  dimension: BreakdownDimensionKey,
  datePreset: string = "last_30d",
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "breakdown", dimension, datePreset],
    queryFn: async (): Promise<BreakdownResult> => {
      const res = await adsApi.getBreakdown(dimension, datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load breakdown");
      }
      return res.data as BreakdownResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    // 4xx is a settled answer (bad dimension / no account) and a 429 is Meta's
    // shared hourly quota being empty — retrying either cannot succeed, and a
    // 429 retry lengthens an org-wide outage. The `2` preserves this query's
    // existing ceiling for transient 5xx/network blips exactly; only the
    // rate-limit branch is new (429 used to be retried twice, because this
    // predicate skipped just 400/401/403).
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  pctOfTop: number | null;
  pctOfPrev: number | null;
  dropOffPct: number | null;
  costPer: number | null;
}

export interface FunnelResult {
  steps: FunnelStep[];
  spend: number;
  date_preset: string;
  currency: string;
  crm_buckets: { won: number; engaged: number; no_response: number; negative: number; open: number };
}

/**
 * Impressions → Clicks → Leads → Engaged → Won funnel. Live Meta account
 * insights + CRM buckets, cached 10 min. Disabled until `enabled` (the Overview
 * tab enables it once the account is connected).
 */
export function useAdsFunnel(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "funnel", datePreset],
    queryFn: async (): Promise<FunnelResult> => {
      const res = await adsApi.getFunnel(datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load funnel");
      }
      return res.data as FunnelResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

// Report metric catalog — mirrors backend REPORT_METRICS (keep in sync).
export const REPORT_METRICS: Array<{ key: string; label: string; kind: string }> = [
  { key: "spend", label: "Spend", kind: "currency" },
  { key: "impressions", label: "Impressions", kind: "number" },
  { key: "reach", label: "Reach", kind: "number" },
  { key: "clicks", label: "Clicks", kind: "number" },
  { key: "leads", label: "Leads", kind: "number" },
  { key: "ctr", label: "CTR", kind: "percent" },
  { key: "cpc", label: "CPC", kind: "currency" },
  { key: "cpm", label: "CPM", kind: "currency" },
  { key: "frequency", label: "Frequency", kind: "number2" },
  { key: "cost_per_lead", label: "Cost / lead", kind: "currency" },
];
export const REPORT_LEVELS: Array<{ key: string; label: string }> = [
  { key: "campaign", label: "Campaigns" },
  { key: "adset", label: "Ad Sets" },
  { key: "ad", label: "Ads" },
];
export const DEFAULT_REPORT_METRICS = ["spend", "impressions", "clicks", "ctr", "leads", "cost_per_lead"];

export interface ReportRequest {
  level: string;
  metrics: string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  date_preset?: string;
}
export interface ReportColumn { key: string; label: string; kind: string }
export interface ReportRow {
  id: string | null;
  name: string;
  status: string | null;
  values: Record<string, number | null>;
}
export interface ReportResult {
  level: string;
  metrics: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: Record<string, number | null>;
  date_preset: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}

/**
 * Custom report. Keyed on the full request so changing metrics/level/sort
 * refetches. Disabled until `enabled` (the tab enables on first build click),
 * so the (relatively heavy) ads fetch only runs when the user asks for it.
 */
export function useAdsCustomReport(req: ReportRequest, enabled: boolean) {
  return useQuery({
    queryKey: ["ads", "custom-report", req],
    queryFn: async (): Promise<ReportResult> => {
      const res = await adsApi.buildCustomReport(req);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to build report");
      return res.data as ReportResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface WeeklyDigest {
  orgName: string;
  periodLabel: string;
  currency: string;
  current: { spend: number; leads: number; cost_per_lead: number };
  deltas: { spend: number | null; leads: number | null; cost_per_lead: number | null };
  topCreatives: Array<{ name: string; kind: string; spend: number; leads: number; cost_per_lead: number | null }>;
  funnel: Array<{ label: string; count: number; pctOfPrev: number | null }>;
  anomaly: { reason: string } | null;
  revenue: { revenue: number; revenuePerSpend: number | null; netProfit: number } | null;
}
export interface WeeklyDigestResult {
  digest: WeeklyDigest;
  html: string;
}

export function useAdsWeeklyDigest(enabled = true) {
  return useQuery({
    queryKey: ["ads", "weekly-digest"],
    queryFn: async (): Promise<WeeklyDigestResult> => {
      const res = await adsApi.getWeeklyDigest();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load weekly digest");
      return res.data as WeeklyDigestResult;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface AnomalyResult {
  anomaly: boolean;
  kind: "cpl_spike" | "spend_spike" | null;
  latest: { date: string; spend: number; leads: number; cost_per_lead: number } | null;
  baselineCpl: number | null;
  baselineSpend: number | null;
  cplRatio: number | null;
  spendRatio: number | null;
  reason: string | null;
  ad_account_id: string;
  currency: string;
  history_days: number;
}

export function useAdsAnomalies(enabled = true) {
  return useQuery({
    queryKey: ["ads", "anomalies"],
    queryFn: async (): Promise<AnomalyResult> => {
      const res = await adsApi.getAnomalies();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load anomalies");
      return res.data as AnomalyResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface CreativeRow {
  creative_key: string;
  creative_id: string | null;
  name: string;
  kind: "video" | "image" | "unknown";
  thumbnail: string | null;
  ad_count: number;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_lead: number | null;
}
export interface VideoRow {
  ad_id: string | null;
  name: string;
  thumbnail: string | null;
  spend: number;
  plays: number;
  thruplays: number;
  p25: number; p50: number; p75: number; p100: number;
  rate25: number | null; rate50: number | null; rate75: number | null; rate100: number | null;
  thruplayRate: number | null;
}
export interface CreativeInsightsResult {
  creatives: CreativeRow[];
  videos: VideoRow[];
  date_preset: string;
  currency: string;
}

export function useAdsCreativeInsights(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "creative-insights", datePreset],
    queryFn: async (): Promise<CreativeInsightsResult> => {
      const res = await adsApi.getCreativeInsights(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load creative insights");
      return res.data as CreativeInsightsResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface PacingAdSet {
  id: string | null;
  name: string | null;
  dailyBudget: number;
  spentToday: number;
  pctOfBudget: number | null;
  alert: "overspend" | "on_track" | "underspend" | "no_budget";
}
export interface PacingResult {
  adsets: PacingAdSet[];
  counts: { overspend: number; on_track: number; underspend: number; no_budget: number };
  projection: {
    spendThisMonth: number;
    dayOfMonth: number;
    daysInMonth: number;
    avgPerDay: number;
    projectedMonthEnd: number;
  };
  currency: string;
}

export function useAdsSpendPacing(enabled = true) {
  return useQuery({
    queryKey: ["ads", "pacing"],
    queryFn: async (): Promise<PacingResult> => {
      const res = await adsApi.getSpendPacing();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load pacing");
      return res.data as PacingResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface RevenuePerLeadResult {
  leads: number;
  sales: number;
  revenue: number;
  spend: number;
  revenuePerLead: number | null;
  revenuePerSale: number | null;
  revenuePerSpend: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
  netProfit: number;
  profitable: boolean;
  date_preset: string;
  currency: string;
}

export function useAdsRevenuePerLead(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "revenue-per-lead", datePreset],
    queryFn: async (): Promise<RevenuePerLeadResult> => {
      const res = await adsApi.getRevenuePerLead(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load revenue per lead");
      return res.data as RevenuePerLeadResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    // See useAdsBreakdown: no retry on 4xx/429, existing 2-retry ceiling kept
    // for transient 5xx/network.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface MultiAccountSummary {
  accounts: Array<{
    ad_account_id: string;
    ad_account_name: string;
    currency: string;
    spend: number;
    leads: number;
    cost_per_lead: number;
    error: string | null;
  }>;
  totals: {
    account_count: number;
    date_preset: string;
    total_spend: number;
    total_leads: number;
    cost_per_lead: number;
    currency: string;
  };
}

export function useMultiAccountSummary() {
  return useMutation({
    mutationFn: async (
      vars: { adAccountIds: string[]; datePreset?: string }
    ): Promise<MultiAccountSummary> => {
      const res = await adsApi.getMultiAccountSummary(vars.adAccountIds, vars.datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load multi-account summary");
      }
      return res.data as MultiAccountSummary;
    },
  });
}

export function useProductCatalogs() {
  return useQuery({
    queryKey: ["ads", "catalogs"],
    queryFn: async () => {
      const res = await adsApi.getProductCatalogs();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProductSets(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-product-sets", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProductSets(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProducts(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-products", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProducts(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProductCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { business_id: string; name: string; vertical?: string }) => {
      const res = await adsApi.createProductCatalog(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalogs"] });
      toast({ variant: "success", title: "Catalog created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create catalog" } });
    },
  });
}

export function useAddProductToCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ catalogId, product }: { catalogId: string; product: any }) => {
      const res = await adsApi.addProductToCatalog(catalogId, product);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalog-products", variables.catalogId] });
      toast({ variant: "success", title: "Product added to catalog" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to add product" } });
    },
  });
}

export function useGenerateMetaAdsFromImage() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, organizationId }: { file: File; organizationId?: string | null }) => {
      return adsApi.generateFromImage(file, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

export function useGenerateMetaAdsFromVideo() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      videoUrl,
      organizationId,
    }: {
      file?: File;
      videoUrl?: string;
      organizationId?: string | null;
    }) => {
      return adsApi.generateFromVideo({ file, videoUrl }, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

// === Mutations ===

export function useAdsSetupConnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.connectAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      // Connecting an account is the moment the user MOST expects the app to
      // notice. `setup-status` alone was not enough once the Meta-backed reads
      // gained long stale windows: the inventory, the Page list behind it, the
      // per-page leadgen state and the new account's balance would all have kept
      // serving pre-connection answers for minutes. Invalidating the exact keys
      // (not all of `["ads"]`) keeps the refresh targeted — nothing unrelated is
      // refetched, so this costs at most the calls the user just earned.
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "balance"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "connected-instagram"] });
      toast({ variant: "success", title: "Ad account connected successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to connect ad account" } });
    },
  });
}

/**
 * Meta's inventory for the connected Facebook user.
 *
 * `includeBusinessScoped` also walks every Business the user administers, which is
 * the ONLY way ad accounts granted through Business Manager appear (/me/adaccounts
 * returns just directly-assigned ones). It costs 2 + 2N Meta calls, so it is part
 * of the query KEY — the cheap page-load read and the thorough picker read are
 * genuinely different queries and must not share a cache entry.
 *
 * THROWS on failure. It used to `return res.data` without checking `res.error`, so
 * a failed Meta call resolved to `undefined` with `isError === false` — every
 * consumer then read `data?.ad_accounts || []` and confidently rendered "every
 * account is already connected" when in truth nothing had loaded. Callers can now
 * tell the two apart.
 */
export function useAvailableAdAccounts(
  enabled = false,
  opts?: { includeBusinessScoped?: boolean }
) {
  const includeBusinessScoped = !!opts?.includeBusinessScoped;
  return useQuery({
    queryKey: ["ads", "available-ad-accounts", { includeBusinessScoped }],
    queryFn: async () => {
      const res = await adsApi.getAvailableAdAccounts({ includeBusinessScoped });
      if (res.error) throw ApiError.fromResult(res, "Failed to load ad accounts from Facebook");
      return res.data;
    },
    enabled,
    // Meta's INVENTORY, and the second-most-expensive read here: a plain call is
    // `GET /me/accounts` + the ad-account lists, and with `includeBusinessScoped`
    // it is `2 + 2N` calls for N businesses. A Facebook Pages / ad-account list
    // changes on the order of weeks, so a 60-second window was spending real
    // quota to re-learn something that had not moved.
    //
    // Invalidated by everything that can actually change it: `useOAuthCallback`
    // and `useAdsSetupConnect` (a new/re-authorized Facebook login is exactly
    // when new accounts appear), `useCreateAdAccount`, and all of `["ads"]` on
    // `useSwitchAdAccount` / `useAdsDisconnect`.
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // A 4xx here is a settled answer ("no ad account connected"), not a blip, and
    // a 429 is the shared hourly bucket being empty — retrying either cannot
    // succeed. The global default reads `error.status`, which ApiError does not
    // set, so without this a 400 would be retried twice for nothing. `2` keeps
    // the existing ceiling for transient 5xx/network.
    retry: (failureCount, error: unknown) => shouldRetryMetaQuery(failureCount, error, 2),
  });
}

export function useSwitchAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.switchAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      // The meta-ads dashboards, setup status, and balance all live under the
      // ["ads", ...] prefix, but the Instagram-only ads dashboard uses
      // ["instagram-ads", ...]. Invalidating both keeps every surface in sync
      // after the user swaps ad accounts — otherwise the old account's ads keep
      // showing because React Query matches by prefix.
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      queryClient.invalidateQueries({ queryKey: ["instagram-ads"] });
      toast({ variant: "success", title: "Ad account switched successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to switch ad account" } });
    },
  });
}

export function useAdsDisconnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    // Pass an ad account id to disconnect THAT account; omit to disconnect the
    // active one. `mutate()` with no argument still works, so existing callers are
    // unaffected.
    mutationFn: async (adAccountId?: string) => {
      const res = await adsApi.disconnect(adAccountId, businessAccountId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      // When the org still holds another ad account the backend promotes it, so the
      // workspace stays connected. Naming it matters: campaigns, insights and
      // conversion tracking all follow the active account, and swapping that
      // silently would leave the user looking at a different account's numbers
      // without being told.
      const r = data as {
        promoted_ad_account_name?: string;
        promoted_ad_account_id?: string;
        disconnected_ad_account_name?: string;
        disconnected_ad_account_id?: string;
        already_disconnected?: boolean;
      } | null;
      const promotedLabel = r?.promoted_ad_account_name || r?.promoted_ad_account_id;
      const removedLabel = r?.disconnected_ad_account_name || r?.disconnected_ad_account_id;

      if (r?.already_disconnected) {
        // Idempotent path — say so rather than claiming a fresh disconnect.
        toast({
          variant: "success",
          title: removedLabel ? `${removedLabel} was already disconnected` : "Already disconnected",
        });
        return;
      }

      toast({
        variant: "success",
        title: removedLabel ? `Disconnected ${removedLabel}` : "Ad account disconnected",
        ...(promotedLabel ? { description: `Now using ${promotedLabel}.` } : {}),
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to disconnect" } });
    },
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createCampaign(data);
      if (res.error) throw ApiError.fromResult(res);
      return { data: res.data, warning: res.warning };
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      if (result?.warning) {
        toast({ title: "Campaign saved as draft", description: result.warning, variant: "default" });
      } else {
        toast({ variant: "success", title: "Campaign created successfully" });
      }
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create campaign" } });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign"] });
      toast({ variant: "success", title: "Campaign updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign deleted" });
    },
  });
}

export function useSyncCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.syncCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      toast({ variant: "success", title: "Campaign synced from Meta" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Sync failed" } });
    },
  });
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.duplicateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign duplicated" });
    },
  });
}

export function useSearchInterests() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchInterests(query),
  });
}

/**
 * Geo search. `mode` narrows Meta's `location_types` — without it a city query
 * competes with regions and countries in one ranked list (the original
 * "city search returns the wrong places" bug) and pincodes are unreachable.
 */
export function useSearchLocations() {
  return useMutation({
    mutationFn: (vars: string | { query: string; mode?: string }) => {
      const { query, mode } = typeof vars === "string" ? { query: vars, mode: undefined } : vars;
      return adsApi.searchLocations(query, mode);
    },
  });
}

/**
 * Estimated CPL derived from the ad account's OWN historical performance.
 * Replaces a hardcoded "₹20 - ₹80". Returns `{available:false, reason}` when
 * the data cannot support a defensible number — render that, never a guess.
 */
export function useCplEstimate(params: {
  objective?: string;
  dailyBudget?: number;
  enabled?: boolean;
}) {
  const { objective, dailyBudget, enabled = true } = params;
  return useQuery({
    queryKey: ["ads", "cpl-estimate", objective, dailyBudget],
    queryFn: async () => {
      const res = await adsApi.getCplEstimate({ objective, daily_budget: dailyBudget });
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        available: boolean; reason?: string; low?: number; high?: number; mid?: number;
        dailyLeadsLow?: number; dailyLeadsHigh?: number; currency?: string;
        basis?: { historicalCpl?: number; historicalLeads?: number };
      };
    },
    enabled,
    // The underlying history moves slowly; no need to refetch on every keystroke.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The Instagram Business account linked to the ad account's SELECTED Facebook
 * Page. Read-only: an IG account links to a page, not to an ad account, so
 * there is nothing to choose — it follows the page selector.
 */
export function useConnectedInstagram(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagram();
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        connected: boolean;
        reason?: string;
        page_id?: string;
        page_name?: string | null;
        instagram?: {
          id: string;
          username: string | null;
          profile_picture_url: string | null;
          followers_count: number | null;
        };
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Real publisher_platform breakdown (Facebook / Instagram / Messenger / AN). */
export function usePlatformBreakdown(datePreset?: string) {
  return useQuery({
    queryKey: ["ads", "platform-breakdown", datePreset],
    queryFn: async () => {
      const res = await adsApi.getPlatformBreakdown(datePreset);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        platforms: Array<{
          platform: string; label: string; spend: number; impressions: number;
          reach: number; clicks: number; leads: number; ctr: number; cpc: number;
          cpm: number; cpl: number | null; spendShare: number;
        }>;
        totals: { spend: number; impressions: number; clicks: number; leads: number; ctr: number; cpc: number; cpl: number | null };
        currency?: string;
      };
    },
    // Live Meta insights, and it has NO `enabled` gate — it fires the moment the
    // Overview tab mounts. Aligned with the other insights reads on the same tab
    // (useAdsFunnel / useAdsCreativeInsights / useAdsRevenuePerLead all use 10
    // min) so one page view does not spend at two different rates for data of
    // the same freshness. Invalidated with all of `["ads"]` on account switch /
    // disconnect, and the Overview Refresh button clears this key explicitly.
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useSearchBehaviors() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchBehaviors(query),
  });
}

export function useSearchJobTitles() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchJobTitles(query),
  });
}

/**
 * Search EVERY detailed-targeting kind at once — interests, behaviours, life
 * events, industries, income, family statuses, devices, OS, job titles.
 *
 * Backed by two different Meta mechanisms (a true `adinterest` search plus the
 * fixed `adTargetingCategory` browse tree); the backend merges them and stamps
 * each row's `type`, which is what decides its spec key.
 */
export function useSearchAllTargeting() {
  return useMutation({
    mutationFn: async (query: string) => {
      const res = await adsApi.searchAllTargeting(query);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
        failedClasses: string[];
      };
    },
  });
}

/** Browse ONE class — the expandable Browse tree. Cached per class. */
export function useBrowseTargeting(cls: string | null) {
  return useQuery({
    queryKey: ["ads", "targeting-browse", cls],
    queryFn: async () => {
      if (!cls) return null;
      const res = await adsApi.browseTargeting(cls);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        class: string;
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
      };
    },
    enabled: !!cls,
    // Meta's category tree is effectively static; don't refetch per expand.
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Audience size for the spec being built. Best-effort by design: Meta refuses
 * an estimate on a too-narrow audience, and that must read as "unavailable"
 * rather than an error.
 */
export function useAudienceEstimate() {
  return useMutation({
    mutationFn: async (vars: { targetingSpec: unknown; optimizationGoal?: string }) => {
      const res = await adsApi.estimateAudience(vars.targetingSpec, vars.optimizationGoal);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { available: boolean; reason?: string; lower?: number; upper?: number };
    },
  });
}

/**
 * Exchanges the Facebook OAuth `code` for a token + the accounts that token can
 * see. This is the endpoint the production incident surfaced as a 500 with
 * `(#4) Application request limit reached` — it is a Meta call, so it can fail
 * on quota like any other.
 *
 * NOT retried: mutations default to `retry: false` app-wide (App.tsx), and an
 * authorization `code` is single-use anyway — a retry would fail as "code
 * already redeemed" even with quota to spare. Callers surface the failure and
 * restart the flow instead.
 */
export function useOAuthCallback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await adsApi.handleCallback(code);
      // Was `return adsApi.handleCallback(code)` — the apiClient swallow-error
      // shape means a `{ success: false, error }` result RESOLVES, so a failed
      // exchange looked successful, `result.data` was undefined and the caller's
      // `if (result?.data)` silently did nothing: the user sat on a spinner-free
      // step 1 with no explanation. Throwing routes it to the caller's catch.
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Could not finish connecting Facebook");
      }
      return res;
    },
    onSuccess: () => {
      // A fresh authorization can widen what the token can see, so anything
      // derived from Meta's account inventory must not keep serving the
      // pre-authorization answer through its (now longer) stale window.
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
    },
  });
}

export function useGetOAuthUrl() {
  return useMutation({
    mutationFn: () => adsApi.getOAuthUrl(),
  });
}

export function useUploadAdImage() {
  return useMutation({
    mutationFn: async (imageUrl: string) => {
      const res = await adsApi.uploadImage(imageUrl);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUpdateMetaAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaAdId, data }: { metaAdId: string; data: any }) => {
      const res = await adsApi.updateMetaAd(metaAdId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad updated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad" } });
    },
  });
}

export function useUpdateMetaCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaCampaignId, status }: { metaCampaignId: string; status: string }) => {
      const res = await adsApi.updateMetaCampaign(metaCampaignId, { status });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Campaign status updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useUpdateMetaAdSet() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaAdSetId,
      status,
      name,
      daily_budget,
    }: {
      metaAdSetId: string;
      status?: string;
      name?: string;
      daily_budget?: number;
    }) => {
      const payload: Record<string, any> = {};
      if (status !== undefined) payload.status = status;
      if (name !== undefined) payload.name = name;
      if (daily_budget !== undefined) payload.daily_budget = daily_budget;
      const res = await adsApi.updateMetaAdSet(metaAdSetId, payload);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      const what =
        variables.name !== undefined
          ? "Ad Set name updated"
          : variables.daily_budget !== undefined
            ? "Ad Set budget updated"
            : "Ad Set status updated";
      toast({ title: what });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad set" } });
    },
  });
}

export function useGenerateAdCopy() {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.generateAdCopy(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

// === LEAD SUPPRESSION ===

export function useSuppressionStatus() {
  return useQuery({
    queryKey: ["ads", "suppression-status"],
    queryFn: async () => {
      const res = await adsApi.getSuppressionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useEnableSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.enableSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to enable suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Lead suppression enabled",
        description: `Applied to ${data?.updated ?? 0} ad set${data?.updated === 1 ? "" : "s"} (${data?.alreadyExcluded ?? 0} already had it).`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to enable suppression" } });
    },
  });
}

export function useApplySuppressionToActive() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.applySuppressionToActive();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to apply suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Suppression re-applied",
        description: `${data?.updated ?? 0} updated, ${data?.alreadyExcluded ?? 0} already had it, ${data?.failures?.length ?? 0} failed.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to apply suppression" } });
    },
  });
}

export function useBackfillSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.backfillSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to backfill suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Backfill complete",
        description: `Uploaded ${data?.uploaded ?? 0} contacts in ${data?.batches ?? 0} batch${data?.batches === 1 ? "" : "es"}.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Backfill failed" } });
    },
  });
}

export function useUploadAdImageFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadImageFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

/**
 * Legacy multipart video upload. LOCAL DEV ONLY — in serverless this returns
 * 413 from API Gateway before Fastify runs, for anything over ~10 MB.
 * Use `useUploadAdVideo` (presigned S3 → Meta by URL) instead.
 */
export function useUploadAdVideoFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadVideoFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

/**
 * Upload an ad video of any size up to Meta's 4 GB ceiling.
 *
 * Two hops, neither of which crosses API Gateway with the payload:
 *   1. Browser PUTs the file straight to S3 via a presigned URL.
 *   2. Backend hands that URL to Meta's /advideos `file_url`.
 *
 * Returns Meta's `{ id }` (the video_id) exactly like the old multipart hook,
 * so call sites only need to swap the mutation input from FormData to a File.
 */
export function useUploadAdVideo() {
  return useMutation({
    mutationFn: async ({
      file,
      title,
      onProgress,
      signal,
    }: {
      file: File;
      title?: string;
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
    }) => {
      // Step 1 — bytes to S3 (this is the part that used to 413).
      const { url, fallbackUrl } = await uploadAdVideoToS3(file, { onProgress, signal });

      // Step 2 — Meta fetches it from that URL. Slow for a large file, so the
      // UI shows a distinct "registering with Meta" phase rather than a stalled
      // 100% progress bar.
      const res = await adsApi.uploadVideoByUrl({
        video_url: url,
        fallback_url: fallbackUrl,
        title: title || file.name.replace(/\.[^.]+$/, ""),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useMetaAdDetail(metaAdId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-ad-detail", metaAdId],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdDetail(metaAdId);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 30 * 1000,
  });
}

// === DRILL-DOWN HOOKS ===

export function useMetaCampaignDetail(metaCampaignId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-campaign-detail", metaCampaignId],
    queryFn: async () => {
      if (!metaCampaignId) return null;
      const res = await adsApi.getMetaCampaignDetail(metaCampaignId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaCampaignId,
    staleTime: 30 * 1000,
    // `retry: 2` here retried a 429 twice — three calls against an exhausted
    // hourly bucket. Same ceiling for transient 5xx/network, but 4xx (429
    // included) is now never repeated.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useMetaAdSetDetail(metaAdSetId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-adset-detail", metaAdSetId],
    queryFn: async () => {
      if (!metaAdSetId) return null;
      const res = await adsApi.getMetaAdSetDetail(metaAdSetId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdSetId,
    staleTime: 30 * 1000,
    // `retry: 2` here retried a 429 twice — three calls against an exhausted
    // hourly bucket. Same ceiling for transient 5xx/network, but 4xx (429
    // included) is now never repeated.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useMetaAdInsights(
  metaAdId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-ad-insights", metaAdId, params],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdInsights(metaAdId, params);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 60 * 1000,
  });
}

export function useAiAssistant() {
  return useMutation({
    mutationFn: async (data: { question: string; context: any }) => {
      const res = await adsApi.aiAssistant(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

// === BUSINESS & FUNDING ===

export function useBusinesses(enabled = false) {
  return useQuery({
    queryKey: ["ads", "businesses"],
    queryFn: async () => {
      const res = await adsApi.getBusinesses();
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      toast({ variant: "success", title: "Ad account created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create ad account" } });
    },
  });
}

export function useFundingDetails(enabled = false) {
  return useQuery({
    queryKey: ["ads", "funding-details"],
    queryFn: async () => {
      const res = await adsApi.getFundingDetails();
      return res.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

// === META LEADS ===

export function useLeadForms() {
  return useQuery({
    queryKey: ["ads", "lead-forms"],
    queryFn: async () => {
      const res = await adsApi.getLeadForms();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateLeadForm() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createLeadForm(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create lead form");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "lead-forms"] });
      toast({ variant: "success", title: "Lead form created", description: "Your Instant Form has been created on Meta." });
    },
    onError: (err: any) => {
      showApiError(err, { fallback: { title: "Failed to create form" } });
    },
  });
}

export function useLeadFormLeads(formId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["ads", "lead-form-leads", formId, limit],
    queryFn: async () => {
      if (!formId) return null;
      const res = await adsApi.getLeadFormLeads(formId, { limit });
      return res.data;
    },
    enabled: !!formId,
    staleTime: 60 * 1000,
  });
}

export function useAllPageLeads(limit = 100, offset = 0) {
  return useQuery({
    queryKey: ["ads", "all-page-leads", limit, offset],
    queryFn: async () => {
      const res = await adsApi.getAllPageLeads({ limit, offset });
      return res.data;
    },
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAdsAudiences() {
  return useQuery({
    queryKey: ["ads", "audiences"],
    queryFn: async () => {
      const res = await adsApi.getAudiences();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useAdsAudiencePresets() {
  return useQuery({
    queryKey: ["ads", "audience-presets"],
    queryFn: async () => {
      const res = await adsApi.getAudiencePresets();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

// === AD ACCOUNT MANAGEMENT ===

export function useAdAgencies(enabled = false) {
  return useQuery({
    queryKey: ["ads", "agencies"],
    queryFn: async () => {
      const res = await adsApi.getAgencies();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdvertisableApplications(enabled = false) {
  return useQuery({
    queryKey: ["ads", "advertisable-applications"],
    queryFn: async () => {
      const res = await adsApi.getAdvertisableApplications();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignedUsers(enabled = false) {
  return useQuery({
    queryKey: ["ads", "assigned-users"],
    queryFn: async () => {
      const res = await adsApi.getAssignedUsers();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) => {
      const res = await adsApi.updateAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      toast({ variant: "success", title: "Ad account updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad account" } });
    },
  });
}

export function useReachFrequencyPredictions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "reach-frequency-predictions"],
    queryFn: async () => {
      const res = await adsApi.getReachFrequencyPredictions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagramAccounts(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram-accounts"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagramAccounts();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === AD COPY & PREVIEWS ===

export function useCopyAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ adId, statusOption }: { adId: string; statusOption?: string }) => {
      const res = await adsApi.copyAd(adId, statusOption ? { status_option: statusOption } : undefined);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad duplicated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to duplicate ad" } });
    },
  });
}

export function useAdPreviews(
  adId: string | null,
  adFormat: string,
  creativeFeature?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "ad-previews", adId, adFormat, creativeFeature],
    queryFn: async () => {
      if (!adId || !adFormat) return null;
      const res = await adsApi.getAdPreviews(adId, {
        ad_format: adFormat,
        ...(creativeFeature ? { creative_feature: creativeFeature } : {}),
      });
      return res.data;
    },
    enabled: !!adId && !!adFormat && enabled,
    staleTime: 60 * 1000,
  });
}

// === ASYNC INSIGHTS ===

export function useCreateInsightsReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      object_id: string;
      level?: string;
      date_preset?: string;
      time_range?: any;
      breakdowns?: string;
      fields?: string;
    }) => {
      const res = await adsApi.createInsightsReport(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create insights report" } });
    },
  });
}

export function useInsightsReport(reportRunId: string | null) {
  return useQuery({
    queryKey: ["ads", "insights-report", reportRunId],
    queryFn: async () => {
      if (!reportRunId) return null;
      const res = await adsApi.pollInsightsReport(reportRunId);
      // MUST throw. apiClient does NOT throw on failure — it returns
      // `{ data: null, success: false, error }`. Returning `res.data` therefore
      // RESOLVED the query on a 429, leaving `query.state.error` null forever, so
      // the rate-limit guard below could never fire and the 3s poll ran until the
      // tab closed (~1,200 calls/hour against a ~4,080/hour app-wide ceiling —
      // and per Meta each call extends the block). It also rendered a
      // falsely-authoritative empty report: `{ isError: false, data: null }` is
      // indistinguishable from a genuinely empty one. Same shape as
      // useAdVideoStatus and as createInsightsReport above.
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!reportRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      // Same rule as useAdVideoStatus: a 3s poll must never grind against an
      // exhausted Meta quota. Reachable only because queryFn throws above.
      if (isMetaRateLimitError(query.state.error)) return false;
      return 3000;
    },
    // A rate limit / 4xx must never be retried; a transient 5xx still may be.
    retry: (count, error) => shouldRetryMetaQuery(count, error, 2),
    staleTime: 0,
  });
}

// === TARGETING ===

export function useBroadTargetingCategories(enabled = false) {
  return useQuery({
    queryKey: ["ads", "broad-targeting-categories"],
    queryFn: async () => {
      const res = await adsApi.getBroadTargetingCategories();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// === PIXELS / DATASETS ===

export function useAdPixels(enabled = false) {
  return useQuery({
    queryKey: ["ads", "pixels"],
    queryFn: async () => {
      const res = await adsApi.getAdPixels();
      // apiClient unwraps { success, data } — res.data is already the array
      return res.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdPixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await adsApi.createAdPixel(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create pixel");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "pixels"] });
    },
  });
}

// === CAPI / CONVERSIONS ===

export function useCapiDatasets(enabled = false) {
  return useQuery({
    queryKey: ["ads", "capi-datasets"],
    queryFn: async () => {
      const res = await adsApi.getCapiDatasets();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomConversions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "custom-conversions"],
    queryFn: async () => {
      const res = await adsApi.getCustomConversions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === PAGE / IG POSTS (media picker) ===

export function usePagePosts(enabled = false) {
  return useQuery({
    queryKey: ["ads", "page-posts"],
    queryFn: async () => {
      const res = await adsApi.getPagePosts(25);
      const payload = res.data;
      // New shape: { pages, posts, warning }. Tolerate the legacy flat-array
      // shape (a server that hasn't been redeployed yet) so the UI never breaks.
      if (Array.isArray(payload)) {
        return { pages: [], posts: payload, warning: null };
      }
      return {
        pages: payload?.pages || [],
        posts: payload?.posts || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstagramMedia(enabled = false) {
  return useQuery({
    queryKey: ["ads", "instagram-media"],
    queryFn: async () => {
      const res = await adsApi.getInstagramMedia(25);
      const payload = res.data;
      // New shape: { accounts, media, warning }. Tolerate the legacy flat-array
      // shape (a server that hasn't been redeployed yet) so the UI never breaks.
      if (Array.isArray(payload)) {
        return { accounts: [], media: payload, warning: null };
      }
      return {
        accounts: payload?.accounts || [],
        media: payload?.media || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === VIDEO UPLOAD BY URL ===

export function useUploadVideoByUrl() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { video_url: string; title?: string }) => {
      const res = await adsApi.uploadVideoByUrl(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Video upload failed" } });
    },
  });
}

/**
 * Poll Meta's processing state for an uploaded ad video.
 *
 * Meta accepts the upload and returns a video_id immediately, then transcodes
 * asynchronously. Building a creative against a video that isn't `ready` gets
 * rejected, so the composer polls until it is. Polling stops on `ready` or
 * `error` — never leaves an interval running.
 */
export function useAdVideoStatus(videoId: string | null) {
  return useQuery({
    queryKey: ["ads", "video-status", videoId],
    queryFn: async () => {
      if (!videoId) return null;
      const res = await adsApi.getVideoStatus(videoId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { id: string; videoStatus: string | null } | null;
    },
    enabled: !!videoId,
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.videoStatus;
      if (status === "ready" || status === "error") return false;
      // NEVER keep polling into an exhausted Meta quota. `data` stays undefined
      // on failure, so without this the 5s poll ran forever against a bucket it
      // could not drain — one stuck upload becoming 720 wasted calls an hour,
      // for every open tab. The poll resumes on the next mount / videoId change,
      // by which time the hourly window has moved.
      if (isMetaRateLimitError(query.state.error)) return false;
      return 5000;
    },
    // A transient poll failure shouldn't blank the badge.
    // `retry: 2` here retried a 429 twice — three calls against an exhausted
    // hourly bucket. Same ceiling for transient 5xx/network, but 4xx (429
    // included) is now never repeated.
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

// === RUN AS AD (T1.3) ===

export interface AdRecommendation {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  post_stats?: {
    reach?: number;
    engagement?: number;
    like_count?: number;
    comment_count?: number;
    share_count?: number;
    saved?: number;
    engagement_rate?: number;
  };
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  cost_per_lead_low?: number;
  cost_per_lead_high?: number;
  target_audience?: {
    age_range?: string;
    gender?: string;
    interests?: string[];
    location_type?: string;
  };
  reason?: string;
  token_usage?: {
    model?: string;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    estimated_cost_usd?: number;
    total_cost_inr?: number;
    charged_cost_inr?: number;
  };
}

export function useAdRecommendation() {
  return useMutation({
    mutationFn: async (data: {
      post?: Record<string, any>;
      baseline?: Record<string, any>;
      image_url?: string;
      business_account_id?: string;
    }): Promise<AdRecommendation> => {
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/suggest-from-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post: data.post || {},
          baseline: data.baseline || null,
          business_account_id: data.business_account_id || null,
          organization_id: orgId || null,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw ApiError.fromResult(json, 'Failed to get recommendation');
      }
      return json as AdRecommendation;
    },
  });
}

export function useCreateAdFromPost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      source: { page_post_id?: string; ig_media_id?: string };
      name?: string;
      objective?: string;
      destination?: string;
      lead_form_id?: string;
      daily_budget?: number;
      targeting_spec?: any;
      business_account_id?: string;
    }) => {
      const res = await adsApi.createAdFromPost(data);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to create ad from post");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success",
        title: "Ad created (paused)",
        description: "Review it in the Ads page, then activate to start delivery.",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Couldn't create the ad" } });
    },
  });
}

// === REVENUE ATTRIBUTION (T2.4) ===

export interface AttributionRow {
  meta_ad_id: string | null;
  ad_name: string | null;
  leads: number;
  meetings: number;
  sales: number;
  revenue: number;
  currency: string;
  source: string;
}

export function useAdAttribution(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "attribution", params],
    queryFn: async (): Promise<AttributionRow[]> => {
      const res = await adsApi.getAttribution(params);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load attribution");
      }
      return (res.data as AttributionRow[]) || [];
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type { CrmLeadQualityRow } from "@/lib/ads/leadQuality";

export function useAdsLeadQuality(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "lead-quality", params],
    queryFn: async (): Promise<import("@/lib/ads/leadQuality").CrmLeadQualityRow[]> => {
      // Soft-fail: never throw — Ads tab must keep working when CRM quality
      // is unavailable (auth blip, 5xx). Empty array → CRM columns show "—".
      try {
        const res = await adsApi.getLeadQuality(params);
        if (res.error || res.success === false) return [];
        return (res.data as import("@/lib/ads/leadQuality").CrmLeadQualityRow[]) || [];
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type CrmLeadStageLead = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  type: string | null;
  created_at: string | null;
};

export type CrmLeadStageBucket = {
  stage_id: string | null;
  stage_name: string;
  stage_color: string | null;
  board_name: string | null;
  count: number;
  is_unassigned: boolean;
  leads?: CrmLeadStageLead[];
  leads_truncated?: boolean;
};

export type CrmLeadStageBreakdown = {
  ad_id: string | null;
  ad_name: string | null;
  match_key: string;
  crm_leads: number;
  stages: CrmLeadStageBucket[];
};

/** Lazy-load per-stage CRM counts when the Ads-tab drawer opens. Soft-fails to null. */
export function useAdsLeadQualityStages(
  params: { adId?: string | null; adName?: string | null; from?: string; to?: string },
  enabled = false
) {
  return useQuery({
    queryKey: ["ads", "lead-quality-stages", params],
    queryFn: async (): Promise<CrmLeadStageBreakdown | null> => {
      try {
        const res = await adsApi.getLeadQualityStages({
          ad_id: params.adId || undefined,
          ad_name: params.adName || undefined,
          from: params.from,
          to: params.to,
        });
        if (res.error || res.success === false) return null;
        return (res.data as CrmLeadStageBreakdown) || null;
      } catch {
        return null;
      }
    },
    enabled:
      enabled &&
      !!(
        (params.adId && String(params.adId).trim()) ||
        (params.adName && String(params.adName).trim())
      ),
    staleTime: 60 * 1000,
    retry: 0,
  });
}

// === AI SUGGEST AD (Phase 3) ===
// On-demand ranking: fetches the org's recent IG posts, calls the we-next-ai
// /meta-ads/rank-posts microservice directly from the frontend, and returns
// ranked promote suggestions. No cron dependency.

export interface AdSuggestion {
  id: string;
  ig_media_id: string | null;
  page_post_id: string | null;
  caption: string;
  thumbnail_url: string | null;
  media_url?: string | null;
  like_count?: number;
  comment_count?: number;
  multiplier: number; // e.g. 4.2 → "4.2x better than average"
  metric: string; // what's outperforming, e.g. "engagement", "reach"
  recommended_objective: string;
  recommended_budget: number; // rupees
  estimated_leads_low: number;
  estimated_leads_high: number;
  reason?: string;
  status: "pending" | "dismissed" | "promoted";
}

export interface PostPromoteSuggestion {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  reason: string;
  ai_source?: string;
}

/**
 * "Should I promote this post?" — calls the real AI service via the wenext
 * backend (T3.3). Fails open server-side, so a resolved result with
 * should_promote:false is the worst case (never a thrown error path here).
 */
export function useSuggestFromPost() {
  return useMutation({
    mutationFn: async (vars: {
      post: Record<string, any>;
      baseline?: Record<string, any>;
      business_account_id?: string;
    }): Promise<PostPromoteSuggestion> => {
      const res = await adsApi.suggestFromPost(vars);
      if (res.error || res.success === false) {
        // Defensive — backend fails open, but never throw a broken UI on a blip.
        return { should_promote: false, reason: "Suggestion unavailable right now.", ai_source: "unavailable" };
      }
      return res.data as PostPromoteSuggestion;
    },
  });
}

/**
 * On-demand rank-posts: fetches the org's IG accounts → their recent posts →
 * calls the we-next-ai /meta-ads/rank-posts microservice directly.
 * Returns only posts the AI says are worth promoting.
 */
export function useAdSuggestions(enabled = true) {
  return useQuery({
    queryKey: ["ads", "suggestions-live"],
    queryFn: async (): Promise<AdSuggestion[]> => {
      // 1. Fetch the org's connected Instagram accounts
      const accountsRes = await apiClient.get('/instagram/accounts');
      const accounts: any[] = accountsRes?.data || [];
      if (accounts.length === 0) return [];

      // 2. Fetch recent posts for each account (parallel)
      const allPosts: any[] = [];
      await Promise.all(
        accounts.map(async (acc: any) => {
          try {
            const res = await apiClient.get('/instagram/automation/posts', {
              params: { instagram_account_id: acc.id, limit: 25 },
            });
            const posts: any[] = res?.data || [];
            allPosts.push(...posts);
          } catch {
            // skip accounts that fail
          }
        })
      );

      if (allPosts.length < 3) return []; // not enough posts for a meaningful baseline

      // 3. Compute baseline
      const engagements = allPosts.map((p: any) => (Number(p.like_count || 0) + Number(p.comment_count || 0)));
      const avgEngagement = engagements.reduce((a: number, b: number) => a + b, 0) / engagements.length;

      // 4. Build the rank-posts payload
      const payloadPosts = allPosts.map((p: any) => ({
        ig_media_id: p.ig_post_id || p.id,
        caption: p.caption || "",
        thumbnail_url: p.thumbnail_url || p.media_url || null,
        engagement: Number(p.like_count || 0) + Number(p.comment_count || 0),
        like_count: Number(p.like_count || 0),
        comment_count: Number(p.comment_count || 0),
      }));

      // 5. Call the microservice directly
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/rank-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          posts: payloadPosts,
          baseline: { avg_engagement: Math.round(avgEngagement * 100) / 100 },
          organization_id: orgId || null,
        }),
      });

      if (!resp.ok) return [];
      const json = await resp.json();
      const suggestions: any[] = json?.suggestions || [];

      // 6. Map back to AdSuggestion shape, keeping only promote-worthy
      return suggestions
        .filter((s: any) => s.should_promote)
        .map((s: any, idx: number) => {
          const post = allPosts[s.index ?? idx];
          return {
            id: post?.ig_post_id || post?.id || `suggestion-${idx}`,
            ig_media_id: post?.ig_post_id || post?.id || null,
            page_post_id: null,
            caption: post?.caption || "",
            thumbnail_url: post?.thumbnail_url || post?.media_url || null,
            media_url: post?.media_url || null,
            like_count: Number(post?.like_count || 0),
            comment_count: Number(post?.comment_count || 0),
            multiplier: Number(s.multiplier || 1),
            metric: s.metric || "engagement",
            recommended_objective: s.recommended_objective || "OUTCOME_LEADS",
            recommended_budget: Number(s.recommended_budget || 500),
            estimated_leads_low: Number(s.estimated_leads_low || 0),
            estimated_leads_high: Number(s.estimated_leads_high || 0),
            reason: s.reason || "",
            status: "pending" as const,
          };
        });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useDismissAdSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adsApi.dismissSuggestion(id);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to dismiss suggestion");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suggestions"] });
    },
  });
}

export { adsApi };
